import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useToast } from '@/components/common/toast';
import { FilterBar, type FilterField } from '@/components/common/FilterBar';
import { Select2 } from '@/components/forms/Select2';
import { Select2Async } from '@/components/forms/Select2Async';
import { DateRangePicker } from '@/components/forms/DateRangePicker';
import { BarChart } from '@/components/charts/BarChart';
import { LineChart } from '@/components/charts/LineChart';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { formatNumber } from '@/utils/format';
import { filterDataApi, filterDataPaged } from '@/api/services/filterData';
import { marketAbuseApi } from '@/api/services/marketAbuse';
import type { MarketAbuseFilterInput, MarketAbuseTableRecord, MarketAbuseTimeSpan } from '@/api/types/marketAbuse';

function yDates() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  const day = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { start: `${day}T00:00:00`, end: `${day}T00:00:00`, max: `${day}T23:59:00` };
}
const num = (v: number | null | undefined, d = 2) => (v == null || Number.isNaN(v) ? '' : formatNumber(v, d));

// Legacy persists the login/uniqueId mode under this localStorage key (TradingVolume/trading-volume-mode.js).
const BY_UNIQUE_ID_KEY = 'MarketAbusebyUniqueId';

interface TVState {
  startDate: string; endDate: string; interval: number; instrument: string | null;
  login: number | null; group: string | null; groupText: string | null; groupProfile: string | null;
  countryId: number | null; portfolioId: number | null; executionVenue: number | null;
  mifidClassification: number | null; dataSourceId: number | null; uniqueId: string | null; byUniqueId: boolean;
}

export function TradingVolumeTab({ canConsolidated, onOpenLogin }: { canConsolidated: boolean; onOpenLogin: (login: number, connector: number) => void }) {
  const toast = useToast();
  const yd = yDates();
  const [draft, setDraft] = useState<TVState>({
    startDate: yd.start, endDate: yd.end, interval: 60, instrument: null, login: null, group: null, groupText: null,
    groupProfile: null, countryId: null, portfolioId: null, executionVenue: null, mifidClassification: null,
    dataSourceId: null, uniqueId: null, byUniqueId: localStorage.getItem(BY_UNIQUE_ID_KEY) === 'true',
  });
  const [applied, setApplied] = useState<MarketAbuseFilterInput | null>(null);
  const [chartType, setChartType] = useState<'bar' | 'line'>('bar');
  // Bumped per View (only for today-ending data) so re-applying identical filters forces
  // a fresh request; static historical results stay cached. react-query keys by value.
  // fetchPulse gives the View button a brief spinner even on a cache hit.
  const [fetchNonce, setFetchNonce] = useState(0);
  const [fetchPulse, setFetchPulse] = useState(false);

  const venues = useQuery({ queryKey: ['rd-filter', 'venues'], queryFn: filterDataApi.getExecutionVenues, staleTime: 5 * 60_000 });
  const mifid = useQuery({ queryKey: ['rd-filter', 'mifid'], queryFn: filterDataApi.getMifidClassifications, staleTime: 5 * 60_000 });
  const connectors = useQuery({ queryKey: ['rd-filter', 'nonTerminalConnectors'], queryFn: filterDataApi.getNonTerminalConnectors, staleTime: 5 * 60_000 });
  const portfolios = useQuery({ queryKey: ['rd-filter', 'portfolios'], queryFn: filterDataApi.getPortfolios, staleTime: 5 * 60_000 });
  const groupProfiles = useQuery({ queryKey: ['rd-filter', 'groupProfiles', 1], queryFn: () => filterDataApi.getGroupProfilesForPage(1), staleTime: 5 * 60_000 });

  const volume = useQuery({ queryKey: ['ma-tv', applied, fetchNonce], queryFn: () => marketAbuseApi.getTradingVolume(applied as MarketAbuseFilterInput), enabled: !!applied });
  const cumulative = useQuery({ queryKey: ['ma-cum', applied, fetchNonce], queryFn: () => marketAbuseApi.getChartTableData(applied as MarketAbuseFilterInput), enabled: !!applied });
  const chart = useQuery({ queryKey: ['ma-chart', applied, fetchNonce], queryFn: () => marketAbuseApi.getChartData(applied as MarketAbuseFilterInput), enabled: !!applied });

  const uid = draft.byUniqueId;
  const str = (v: string | number | null) => (v == null ? '' : String(v));

  const view = () => {
    if (!draft.instrument) { toast.error('Choosing an Instrument is mandatory'); return; }
    if (!draft.startDate) { toast.error('Please select a Date & Time Range'); return; }
    const interval = !draft.interval || draft.interval === 0 ? 30 : draft.interval;
    setApplied({
      startDate: draft.startDate, endDate: draft.endDate, interval, instrument: draft.instrument,
      countryId: draft.countryId ?? null, dataSourceId: uid ? null : draft.dataSourceId ?? null,
      group: draft.group || null, groupText: draft.groupText || null, groupProfile: draft.groupProfile || null,
      executionVenue: draft.executionVenue ?? null, mifidClassification: draft.mifidClassification ?? null,
      login: uid ? null : draft.login ?? null, uniqueId: uid ? draft.uniqueId || null : null,
      byUniqueId: uid, portfolioId: uid ? null : draft.portfolioId ?? null, skipCount: 0, maxResultCount: 1000,
    });
    if (new Date(draft.endDate).toDateString() === new Date().toDateString()) setFetchNonce((n) => n + 1);
    setFetchPulse(true);
    window.setTimeout(() => setFetchPulse(false), 450);
  };

  const fields: (React.ReactNode | FilterField)[] = [
    {
      node: (
        <div key="date">
          <label className="form-label">Date & Time Range</label>
          <DateRangePicker mode="range" enableTime timeSeconds maxDate={yd.max} value={{ start: draft.startDate, end: draft.endDate }} onChange={(v) => setDraft((f) => ({ ...f, startDate: v.start, endDate: v.end }))} />
        </div>
      ),
      pinned: true,
      width: 270,
    },
    <div key="instrument">
      <label className="form-label">Instrument</label>
      <Select2Async placeholder="Select" value={str(draft.instrument) || null} fetch={filterDataPaged.instruments} onChange={(v) => setDraft((f) => ({ ...f, instrument: v || null }))} />
    </div>,
    ...(!uid ? [
      <div key="login"><label className="form-label">Login</label><input className="form-control" inputMode="numeric" value={draft.login ?? ''} onChange={(e) => setDraft((f) => ({ ...f, login: e.target.value ? Number(e.target.value) : null }))} /></div>,
    ] : []),
    <div key="group"><label className="form-label">Group</label><Select2Async placeholder="All" value={str(draft.group) || null} fetch={filterDataPaged.groups} onChange={(v) => setDraft((f) => ({ ...f, group: v || null }))} /></div>,
    <Select2 key="groupProfile" label="Group Profile" allowClear placeholder="All" value={str(draft.groupProfile)} options={groupProfiles.data ?? []} onChange={(v) => setDraft((f) => ({ ...f, groupProfile: v || null }))} />,
    <div key="groupText"><label className="form-label">Group Text Filter</label><input className="form-control" placeholder="S*, *abc*, !SPOT" value={draft.groupText ?? ''} onChange={(e) => setDraft((f) => ({ ...f, groupText: e.target.value || null }))} /></div>,
    <div key="interval"><label className="form-label">Interval</label><div className="input-group input-group-sm"><input className="form-control" inputMode="numeric" value={draft.interval || ''} onChange={(e) => setDraft((f) => ({ ...f, interval: Number(e.target.value) || 0 }))} /><span className="input-group-text">seconds</span></div></div>,
    <div key="country"><label className="form-label">Country</label><Select2Async placeholder="All" value={str(draft.countryId) || null} fetch={filterDataPaged.countries} onChange={(v) => setDraft((f) => ({ ...f, countryId: v ? Number(v) : null }))} /></div>,
    ...(!uid ? [
      <Select2 key="portfolio" label="Portfolio Profile" allowClear placeholder="All" value={str(draft.portfolioId)} options={portfolios.data ?? []} onChange={(v) => setDraft((f) => ({ ...f, portfolioId: v ? Number(v) : null }))} />,
    ] : []),
    <Select2 key="venue" label="Execution Venue" allowClear placeholder="All" value={str(draft.executionVenue)} options={venues.data ?? []} onChange={(v) => setDraft((f) => ({ ...f, executionVenue: v ? Number(v) : null }))} />,
    <Select2 key="mifid" label="MiFID" allowClear placeholder="All" value={str(draft.mifidClassification)} options={mifid.data ?? []} onChange={(v) => setDraft((f) => ({ ...f, mifidClassification: v ? Number(v) : null }))} />,
    ...(!uid ? [
      <Select2 key="connector" label="Connector" allowClear placeholder="All" value={str(draft.dataSourceId)} options={connectors.data ?? []} onChange={(v) => setDraft((f) => ({ ...f, dataSourceId: v ? Number(v) : null }))} />,
    ] : [
      <div key="uniqueId"><label className="form-label">Unique ID</label><input className="form-control" value={draft.uniqueId ?? ''} onChange={(e) => setDraft((f) => ({ ...f, uniqueId: e.target.value || null }))} /></div>,
    ]),
  ];

  const volRows = volume.data?.items ?? [];
  const cumRows = cumulative.data?.items ?? [];

  const loginVolCols: DataGridColumn<MarketAbuseTableRecord>[] = [
    { key: 'login', title: 'Login', value: (r) => r.login, cell: (r) => <a href="#" className="fw-semibold" onClick={(e) => { e.preventDefault(); if (r.dataSourceId) onOpenLogin(r.login, r.dataSourceId); }}>{r.login}</a> },
    { key: 'connector', title: 'Connector', value: (r) => r.connector },
    { key: 'symbol', title: 'Instrument', value: (r) => r.symbol },
    { key: 'notionalAmountRc', title: 'Volumes Traded (RC)', className: 'text-end', value: (r) => r.notionalAmountRc, cell: (r) => num(r.notionalAmountRc, 0) },
    { key: 'executionVenue', title: 'Execution Venue', value: (r) => r.executionVenue },
    { key: 'country', title: 'Country', value: (r) => r.country },
    { key: 'miFIDClassification', title: 'MiFID Classification', value: (r) => r.miFIDClassification },
    { key: 'vwap', title: 'VWAP', className: 'text-end', value: (r) => r.vwap, cell: (r) => num(r.vwap, 3) },
  ];
  const uidVolCols: DataGridColumn<MarketAbuseTableRecord>[] = [
    { key: 'uniqueId', title: 'Unique Id', value: (r) => r.uniqueId },
    ...loginVolCols.slice(2),
  ];
  const loginCumCols: DataGridColumn<MarketAbuseTimeSpan>[] = [
    { key: 'loginDisplay', title: 'Login', value: (r) => r.loginDisplay },
    { key: 'symbol', title: 'Instrument', value: (r) => r.symbol },
    { key: 'timeInterval', title: 'Time Interval', value: (r) => r.timeInterval },
    { key: 'notionalAmountRc', title: 'Volumes Traded (RC)', className: 'text-end', value: (r) => r.notionalAmountRc, cell: (r) => num(r.notionalAmountRc, 0) },
    { key: 'cumulativeNotionalRc', title: 'Cumulative Volume (RC)', className: 'text-end', value: (r) => r.cumulativeNotionalRc, cell: (r) => num(r.cumulativeNotionalRc, 0) },
  ];
  const uidCumCols: DataGridColumn<MarketAbuseTimeSpan>[] = [
    { key: 'uniqueId', title: 'Unique Id', value: (r) => r.uniqueId },
    ...loginCumCols.slice(1),
  ];

  const series = useMemo(() => (chart.data?.datasets ?? []).map((d) => ({ label: d.label, data: d.data.map((x) => x ?? 0) })), [chart.data]);

  return (
    <>
      <FilterBar
        card={false}
        fields={fields}
        visibleCount={4}
        modes={canConsolidated ? <div className="form-check form-switch mb-0"><input className="form-check-input" type="checkbox" role="switch" id="ma-byuid" checked={draft.byUniqueId} onChange={(e) => { localStorage.setItem(BY_UNIQUE_ID_KEY, String(e.target.checked)); setDraft((f) => ({ ...f, byUniqueId: e.target.checked, login: null, dataSourceId: null, portfolioId: null, uniqueId: null })); }} /><label className="form-check-label small" htmlFor="ma-byuid">By Unique ID</label></div> : undefined}
        actions={<button type="button" className="btn btn-primary btn-sm" onClick={view} disabled={volume.isFetching || fetchPulse}>{volume.isFetching || fetchPulse ? <i className="fa fa-spinner fa-spin me-1" /> : <i className="fa fa-search me-1" />}View</button>}
      />

      {/* Main trading-volume table (full width), then chart + cumulative side by side — legacy order. */}
      <div className="border-top pt-2 mt-2 mb-3">
        {uid
          ? <DataGrid columns={uidVolCols} data={volRows} rowKey={(r) => `${r.uniqueId}-${r.symbol}-${r.dataSourceId}`} pageSize={5} card={false} includeExcel excelFileName="Trading Volume" emptyMessage="No data." />
          : <DataGrid columns={loginVolCols} data={volRows} rowKey={(r) => `${r.login}-${r.symbol}-${r.dataSourceId}`} pageSize={5} card={false} includeExcel excelFileName="Trading Volume" emptyMessage="No data." />}
      </div>

      {/* Vertical split between the chart (left) and the cumulative table (right). */}
      <div className="row g-0">
        <div className="col-lg-6 border-end pe-lg-3">
          <div className="card custom-card mb-0 h-100">
            <div className="card-header border-bottom d-flex align-items-center">
              <h6 className="card-title mb-0 me-auto">Volumes Traded (RC)</h6>
              <div style={{ minWidth: 140 }}>
                <Select2 searchable={false} allowClear={false} value={chartType} options={[{ value: 'bar', label: 'Bar Chart' }, { value: 'line', label: 'Line Chart' }]} onChange={(v) => setChartType((v as 'bar' | 'line') || 'bar')} />
              </div>
            </div>
            <div className="card-body" style={{ minHeight: 400 }}>
              {chartType === 'bar'
                ? <BarChart labels={chart.data?.labels ?? []} series={series} height={340} emptyHint={!applied ? 'Choose an instrument and click View.' : 'No data.'} />
                : <LineChart labels={chart.data?.labels ?? []} series={series} height={340} emptyHint={!applied ? 'Choose an instrument and click View.' : 'No data.'} />}
            </div>
          </div>
        </div>
        <div className="col-lg-6 ps-lg-3">
          {uid
            ? <DataGrid columns={uidCumCols} data={cumRows} rowKey={(r) => `${r.uniqueId}-${r.symbol}-${r.timeInterval}`} pageSize={5} card fillHeight columnVisibilityToggle={false} includeExcel excelFileName="Cumulative Notional" emptyMessage="No data." />
            : <DataGrid columns={loginCumCols} data={cumRows} rowKey={(r) => `${r.loginDisplay}-${r.symbol}-${r.timeInterval}`} pageSize={5} card fillHeight columnVisibilityToggle={false} includeExcel excelFileName="Cumulative Notional" emptyMessage="No data." />}
        </div>
      </div>
    </>
  );
}
